<!-- eslint-disable vue/valid-v-slot -->
<template>
    <TemplateModalEx v-model:show="computedShow" class="googleAccountAgentSettingAddDialogDetails" :showFooter="false">
        <template #title>追加</template>
        <template #body>
            <Stepper v-if="computedShow" :items="stepperTitle" :step="stepperStep" @update:step="onNext"
                :nextDisabled="nextDisabled" @cancel="onClose" @apply="onApply">
                <template #item.1>
                    <div class="w-100">
                        <SelectAndroidAgent ref="selectAndroidAgentRef" v-model:selected-agent-ids="selectedAgentIds"
                            :unconfiguredAgents="unconfiguredAgents" :divisionList="divisionList" />
                    </div>
                </template>
                <template #item.2>
                    <StepperItem :backButtonLabel="'端末選択に戻る'" :applyButtonLabel="'OK'">
                        <GoogleAccountAgentSettingEdit ref="editComponentRef" :mode=0
                            :targetAssets="targetAssetsForEdit" v-model:settingData="configSettingData"
                            :listHeight="200" />
                    </StepperItem>
                </template>
            </Stepper>
        </template>
    </TemplateModalEx>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import SelectAndroidAgent from '@/views/Mobile/Android/GoogleAccountSetting/SelectAndroidAgent.vue';
import GoogleAccountAgentSettingEdit from '@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingEdit.vue';
import { AndroidSimpleAsset } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAsset';
import { Stepper, StepperItem, TemplateModalEx } from 'lib-vue-v2/components';
import { DivisionDBData } from '@/models/DivisionData';
import { AMAPI_WorkAccountSetupConfig } from '@/models/Mobile/Android/AndroidManagementV1Schema';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';

interface Props {
    show: boolean;
    unconfiguredAgents: Array<AndroidSimpleAsset>;
    divisionList: Array<DivisionDBData>;
}
const props = withDefaults(defineProps<Props>(), {});

// 親に適用された「部署データ」と、今回入力された「アカウント設定データ」を返すインターフェース
export interface ApplyPayload {
    divisionData: {
        divisionId: number;
        enableChildDivision: boolean;
    };
    selectedAgentIds: Set<number>;
    settingData: AMAPI_WorkAccountSetupConfig;
}

interface Emits {
    (event: "update:show", newValue: boolean): void;
    (event: "apply", payload: ApplyPayload): void;
}
const emit = defineEmits<Emits>();

// 選択中
const selectAndroidAgentRef = ref<any>(null);
const editComponentRef = ref<InstanceType<typeof GoogleAccountAgentSettingEdit> | null>(null);
const selectedAgentIds = ref<Set<number>>(new Set());

// 表示非表示の切り替え
const refShow = ref(false);
const computedShow = computed({
    get: () => refShow.value,
    set: (newValue: boolean) => emit("update:show", newValue)
});

// --- 部署データの退避用オブジェクト ---
const savedDivisionData = ref({
    divisionId: 0,
    enableChildDivision: false
});

// アカウント設定データの保持用オブジェクト
const configSettingData = ref<AMAPI_WorkAccountSetupConfig>({
    authenticationType: schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces,
    requiredAccountEmail: ''
});

// --- Computed ---
const nextDisabled = computed(() => {
    return selectedAgentIds.value.size == 0;
});

const targetAssetsForEdit = computed(() => {
    if (!props.unconfiguredAgents || selectedAgentIds.value.size === 0) {
        return [];
    }
    return props.unconfiguredAgents.filter(asset =>
        selectedAgentIds.value.has(asset.agentNo)
    );
});

// stepper
const stepperTitle = ["1.端末選択", "2.Googleアカウントでのログイン設定"];
const stepperStep = ref(0);

// 初期設定値（デフォルト値）へのリセット処理
const resetToDefaultSettings = () => {
    configSettingData.value = {
        authenticationType: schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces,
        requiredAccountEmail: ''
    };
    savedDivisionData.value = {
        divisionId: 0,
        enableChildDivision: false
    };
};

// 表示情報の監視
watch(() => props.show, (newVal) => {
    refShow.value = newVal;
    if (newVal) {
        stepperStep.value = 0;
        selectedAgentIds.value = new Set();
        resetToDefaultSettings();
    }
}, { immediate: true });

// stepper遷移
const onNext = (nextStep: number): void => {
    if (stepperStep.value === 0 && nextStep === 1) {
        if (selectAndroidAgentRef.value) {
            const data = selectAndroidAgentRef.value.getSelectedDivisionData();
            savedDivisionData.value = {
                divisionId: data.divisionId,
                enableChildDivision: data.enableChildDivision
            };
        }
    }
    stepperStep.value = nextStep;
}

// 適用ボタン押下
const onApply = (): void => {
    if (!editComponentRef.value) return;
    const isValid = editComponentRef.value.validateCheck();
    if (!isValid) return;
    emit("apply", {
        divisionData: savedDivisionData.value,
        selectedAgentIds: selectedAgentIds.value,
        settingData: configSettingData.value
    });
    emit("update:show", false);
}

// 閉じるボタン押下
const onClose = (): void => {
    emit("update:show", false);
}
</script>

<style lang="scss" scoped>
.googleAccountAgentSettingAddDialogDetails {
    :deep(.modal-content) {
        width: 960px;
        height: 692px;

        .stepper {
            .stepperContent {
                margin: 0px 0px 24px 0px;
            }
        }
    }

    .googleAccountAgentSettingDialogFooter {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        height: 40px;

        >button {
            position: static;
            transform: none;
        }
    }
}

.custom-wide-modal :deep(.modal-dialog) {
    max-width: 1000px !important;
    width: 100%;
}
</style>
